const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/store/setup/rootReducer.ts';
import Imm from 'immutable';
import { Action, combineReducers } from 'redux';
import { getConfiguration } from 'App/utils/clientSetupGetRessource';
import { RESET_STATE } from '@cistec/common_web/actions/userActions';
import { CommonStoreState, makeReducersWithCatch } from '@cistec/common_web/reducers';
import { metakatReducerMultiMetakat } from '@cistec/common_web/reducers/metakatReducer';
import { networkReducer } from '@cistec/common_web/reducers/networkReducer';
import deviceReducer from '@cistec/common_web/reducers/deviceReducer';
import { intlReducer } from '@cistec/common_web/reducers/intlReducer';
import { casesReducer } from '@cistec/common_web/reducers/casesReducer';
import { kompendiumReducer } from '@cistec/common_web/reducers/kompendiumReducer';
import { labReportsReducer } from '@cistec/common_web/reducers/labReportsReducer';
import { ordersReducer } from '@cistec/common_web/reducers/ordersReducer';
import { showUncaughtErrorFromRoot } from '@cistec/common_web/reducers/uiReducer';
import { userReducer } from '@cistec/common_web/reducers/userReducer';
import { cis4mReducer } from '@cistec/common_web/modules/CIS4m/store';
import { appointmentReducer } from '../appointment';
import { appointmentConfirmationReducer } from '../appointmentConfirmation';
import { arbeitgeberReducer } from '../arbeitgeber';
import { userLinkageReducer } from '../userLinkage';
import { eCheckinEditorReducer } from 'App/components/eCheckin/store';
import { eCheckinItemsReducer } from '../eCheckinItems';
import { externalLinkReducer } from '../externalLink';
import { featureFlagsReducer } from '../featureFlags';
import { iamTokenReducer } from '../iam';
import { loginReducer } from '../login';
import { nativeAppAuthReducer } from '../nativeAppAuth';
import { registrationStatus } from '../registrationStatus';
import { relativesReducer } from '../relative';
import { documentsReducer } from '../documents';
import { responsibilityReducer } from '../zustaendigkeit';
import { schedulingPlansReducer } from '../schedulingPlan';
import { schedulingSlotsReducer } from '../schedulingSlot';
import { searchGepartnerReducer } from '../gepartnerSearch';
import { synedraTokenReducer } from '../synedra';
import { trackingReducer } from '../tracking';
import { uiReducer } from '../ui';
import { workItemReducer } from '../workItem';
import { messagesReducer } from '../messages';
import { sublimdReducer } from '../sublimd';
import { appointmentAttachmentReducer } from '../appointmentAttachment';
import { ReducerState } from '@cistec/common_web/defs/actionTypes';
import { allowedAppointmentTypesReducer } from 'App/store/allowedAppointmentTypes';
import { formReducer } from '@cistec/common_web/components/Forms/store';
import { allowedEcheckinAppointmentSettingsReducer } from '../allowedECheckinAppointmentTypes';
import { patientsReducer as commonPatientsReducer } from '@cistec/common_web/reducers/patientsReducer';
import { routerReducer } from './routerHistory';

const { customMetaKatMultipleKeys: multiMetakatKeys } = getConfiguration();

export interface PatAppStoreState extends CommonStoreState {
    appointment: ReducerState;
    allowedAppointmentTypes: ReducerState;
    allowedEcheckinAppointmentSettings: ReducerState;
    appointmentAttachment: ReducerState;
    appointmentConfirmation: ReducerState;
    arbeitgeber: ReducerState;
    checkRegristrationStatus: ReducerState;
    documents: ReducerState;
    eCheckinEditor: ReducerState;
    eCheckinItems: ReducerState;
    externalLink: ReducerState;
    featureFlags: ReducerState;
    iamToken: ReducerState;
    iamUserPatientLink: ReducerState;
    login: ReducerState;
    messages: ReducerState;
    metakat: ReducerState;
    nativeAppAuth: ReducerState;
    patients: ReducerState;
    relatives: ReducerState;
    responsibility: ReducerState;
    router: ReducerState;
    schedulingPlan: ReducerState;
    schedulingSlots: ReducerState;
    searchGepartners: ReducerState;
    sublimd: ReducerState;
    synedraToken: ReducerState;
    tracking: ReducerState;
    ui: ReducerState;
    workItem: ReducerState;
}

export const commonReducers = {
    patients: commonPatientsReducer,
    cases: casesReducer,
    device: deviceReducer,
    cis4m: cis4mReducer,
    form: formReducer,
    i18n: intlReducer,
    kompendium: kompendiumReducer,
    labReports: labReportsReducer,
    network: networkReducer,
    orders: ordersReducer,
    user: userReducer,
};

const reducers = () => ({
    ...commonReducers,
    appointment: appointmentReducer,
    allowedAppointmentTypes: allowedAppointmentTypesReducer,
    allowedEcheckinAppointmentSettings: allowedEcheckinAppointmentSettingsReducer,
    appointmentAttachment: appointmentAttachmentReducer,
    appointmentConfirmation: appointmentConfirmationReducer,
    arbeitgeber: arbeitgeberReducer,
    checkRegristrationStatus: registrationStatus,
    documents: documentsReducer,
    eCheckinEditor: eCheckinEditorReducer,
    eCheckinItems: eCheckinItemsReducer,
    externalLink: externalLinkReducer,
    featureFlags: featureFlagsReducer,
    iamToken: iamTokenReducer,
    iamUserPatientLink: userLinkageReducer,
    login: loginReducer,
    messages: messagesReducer,
    metakat: metakatReducerMultiMetakat(Imm.fromJS(multiMetakatKeys)),
    nativeAppAuth: nativeAppAuthReducer,
    relatives: relativesReducer,
    responsibility: responsibilityReducer,
    router: routerReducer,
    schedulingPlan: schedulingPlansReducer,
    schedulingSlots: schedulingSlotsReducer,
    searchGepartners: searchGepartnerReducer,
    sublimd: sublimdReducer,
    synedraToken: synedraTokenReducer,
    tracking: trackingReducer,
    ui: uiReducer,
    workItem: workItemReducer,
});

export const rootReducer = () => {
    return ((combinedReducers: any) => {
        const initialState = combinedReducers(undefined, {} as Action);
        return (state: any = initialState, action: any = {}) => {
            try {
                switch (action.type) {
                    case RESET_STATE:
                        // TODO: reset state does not trigger reload of data i.e. featureflags, we should keep these entries in state when resetting
                        return Object.assign(initialState, {
                            device: state.device,
                            i18n: state.i18n,
                            tracking: state.tracking,
                            ui: state.ui,
                            messages: state.messages,
                            featureFlags: state.featureFlags,
                        });
                    default:
                        return combinedReducers(state, action);
                }
            } catch (exc) {
                return showUncaughtErrorFromRoot(state, exc.origin || '???', exc);
            }
        };
    })(combineReducers(makeReducersWithCatch(reducers() as any)));
};
